/**
 * Site Refresh 2026: homepage video section (A16, loop by A45). Markup: snippets/refresh-videos.liquid.
 *
 * Loop: one video plays at a time, muted, in the wide (active) card; the others are slices
 * showing their poster. On the active video's end the next one opens and plays; after the last
 * it loops to the first. Plays only while the track is at least half on screen and the tab is
 * visible. Only the active video gets a src (preload auto); the others stay posters with
 * preload none until they become active. Pause stops the loop (WCAG 2.2.2); hover and focus
 * don't. Reduced motion: starts paused on video 1, no autoplay, and stops at the end of a video.
 * When not every slice fits (narrow screens; phone: the next two only), the active card comes
 * first, followed by the next slices in loop order.
 * Viewer: clicking the active card opens a full-screen dialog with the video, sound and
 * controls, a counter, Close (44px), prev/next, arrow keys, swipe and Esc. Focus is trapped and
 * returns to the card. The loop waits while it's open and resumes on close (on the next video
 * if the active one finished in the viewer).
 */
(() => {
	if (window.__rvInit) return;
	window.__rvInit = true;

	const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
	const phone = window.matchMedia('(max-width: 767px)');
	// Inline loop: the HLS playlist where the browser plays it natively (picks a small rendition
	// for the card), else the mp4
	const canHls = !!document.createElement('video').canPlayType('application/vnd.apple.mpegurl');
	const ICONS = {
		close: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6L6 18"/></svg>',
		prev: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" aria-hidden="true" focusable="false"><path d="M15 6l-6 6 6 6"/></svg>',
		next: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" aria-hidden="true" focusable="false"><path d="M9 6l6 6-6 6"/></svg>',
	};
	// Slice limits per layout: smallest slice, gap, smallest wide card, most slices shown
	const LAYOUT = {
		phone: { slice: 40, gap: 10, active: 200, max: 2 },
		wide: { slice: 44, gap: 12, active: 280, max: Infinity },
	};

	function init(root) {
		if (root.__rv) return;
		root.__rv = true;
		const track = root.querySelector('.js-rv-track');
		const itemEls = [...root.querySelectorAll('.js-rv-item')];
		const segs = [...root.querySelectorAll('.js-rv-seg')];
		const countEl = root.querySelector('.js-rv-count');
		const prevBtn = root.querySelector('.js-rv-prev');
		const nextBtn = root.querySelector('.js-rv-next');
		const pauseBtn = root.querySelector('.js-rv-pause');
		const items = itemEls.map((el) => {
			const card = el.querySelector('.js-rv-card');
			return {
				el,
				card,
				video: el.querySelector('.js-rv-video'),
				bar: el.querySelector('.js-rv-bar'),
				src: card.getAttribute('data-rv-src'),
				inline: (card.getAttribute('data-rv-hls') && canHls) ? card.getAttribute('data-rv-hls') : card.getAttribute('data-rv-src'),
				poster: card.getAttribute('data-rv-poster'),
				labelIdle: card.getAttribute('data-rv-label-idle'),
				labelActive: card.getAttribute('data-rv-label-active'),
				counter: card.getAttribute('data-rv-counter'),
			};
		});
		const N = items.length;
		if (!N || !track) return;
		const text = {
			close: root.getAttribute('data-rv-close'),
			prev: root.getAttribute('data-rv-prev'),
			next: root.getAttribute('data-rv-next'),
			pause: root.getAttribute('data-rv-pause'),
			play: root.getAttribute('data-rv-play'),
			title: root.getAttribute('data-rv-title'),
		};

		let active = 0;
		let paused = reduced.matches;
		let inView = false;
		let viewerOpen = false;
		let raf = 0;

		const wrap = (i) => ((i % N) + N) % N;

		// Progress: the gold bar on the active card and the 7 segments
		function setProgress(p) {
			items.forEach((it, i) => {
				if (it.bar) it.bar.style.transform = `scaleX(${i === active ? p : 0})`;
			});
			segs.forEach((s, i) => {
				s.style.transform = `scaleX(${i < active ? 1 : i === active ? p : 0})`;
			});
		}
		function progressOf(v) {
			return v && v.duration && isFinite(v.duration) ? Math.min(1, v.currentTime / v.duration) : 0;
		}
		function tick() {
			raf = 0;
			const v = items[active].video;
			setProgress(progressOf(v));
			if (v && !v.paused && !v.ended) raf = requestAnimationFrame(tick);
		}
		function startTick() {
			if (!raf) raf = requestAnimationFrame(tick);
		}

		// Which slices are shown, and in which order
		function layout() {
			const L = phone.matches ? LAYOUT.phone : LAYOUT.wide;
			const w = track.clientWidth;
			let k = Math.floor((w - L.active) / (L.slice + L.gap));
			k = Math.max(0, Math.min(N - 1, L.max, k));
			const all = k >= N - 1;
			items.forEach((it, i) => {
				const rel = wrap(i - active);
				const show = rel <= k;
				if (!show && it.el.contains(document.activeElement)) items[active].card.focus({ preventScroll: true });
				it.el.hidden = !show;
				it.el.style.order = all ? '' : String(rel);
			});
		}

		function renderState() {
			items.forEach((it, i) => {
				const on = i === active;
				it.el.classList.toggle('is-active', on);
				if (on) {
					it.card.setAttribute('aria-current', 'true');
					it.card.setAttribute('aria-haspopup', 'dialog');
					it.card.setAttribute('aria-label', it.labelActive);
				} else {
					it.card.removeAttribute('aria-current');
					it.card.removeAttribute('aria-haspopup');
					it.card.setAttribute('aria-label', it.labelIdle);
				}
			});
			if (countEl && items[active].counter) countEl.textContent = items[active].counter;
			layout();
		}

		function renderPause() {
			root.classList.toggle('is-paused', paused);
			if (pauseBtn) pauseBtn.setAttribute('aria-label', paused ? text.play : text.pause);
		}

		// Play the active video when it should, pause it otherwise
		function sync() {
			renderPause();
			const it = items[active];
			const v = it.video;
			if (!v || !it.inline) return;
			const want = !paused && inView && !viewerOpen && !document.hidden;
			if (!want) {
				v.pause();
				return;
			}
			if (!v.getAttribute('src')) {
				v.preload = 'auto';
				v.setAttribute('src', it.inline);
			}
			v.muted = true;
			const p = v.play();
			if (p && p.catch) {
				p.catch((err) => {
					// Autoplay blocked: show the poster, paused, with Play on the button
					if (err && err.name === 'NotAllowedError' && items[active] === it) {
						paused = true;
						renderPause();
					}
				});
			}
		}

		function go(i, opts = {}) {
			const next = wrap(i);
			const old = items[active];
			if (old.video) old.video.pause();
			if (next !== active) old.el.classList.remove('is-loaded');
			active = next;
			const v = items[active].video;
			if (v && v.getAttribute('src')) {
				try { v.currentTime = 0; } catch (e) { /* not seekable yet */ }
			}
			if (opts.play) paused = false;
			setProgress(0);
			renderState();
			sync();
		}

		items.forEach((it, i) => {
			const v = it.video;
			if (v) {
				v.addEventListener('playing', () => {
					if (i !== active) return;
					it.el.classList.add('is-loaded');
					startTick();
				});
				v.addEventListener('ended', () => {
					if (i !== active) return;
					setProgress(1);
					if (reduced.matches) {
						paused = true;
						renderPause();
						return;
					}
					go(active + 1);
				});
			}
			it.card.addEventListener('click', () => {
				if (i === active) open(i, it.card);
				else go(i, { play: true });
			});
		});

		if (prevBtn) prevBtn.addEventListener('click', () => go(active - 1));
		if (nextBtn) nextBtn.addEventListener('click', () => go(active + 1));
		if (pauseBtn) {
			pauseBtn.addEventListener('click', () => {
				paused = !paused;
				sync();
			});
		}

		// Phone: swipe the track for next / previous
		let sx = null;
		let sy = 0;
		track.addEventListener('touchstart', (e) => {
			if (e.touches.length === 1) { sx = e.touches[0].clientX; sy = e.touches[0].clientY; }
		}, { passive: true });
		track.addEventListener('touchend', (e) => {
			if (sx === null) return;
			const dx = e.changedTouches[0].clientX - sx;
			const dy = e.changedTouches[0].clientY - sy;
			sx = null;
			if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) go(active + (dx < 0 ? 1 : -1));
		}, { passive: true });

		// Only on screen, never in a background tab
		if ('IntersectionObserver' in window) {
			new IntersectionObserver((entries) => {
				entries.forEach((e) => { inView = e.isIntersecting && e.intersectionRatio >= 0.5; });
				sync();
			}, { threshold: [0, 0.5] }).observe(track);
		}
		document.addEventListener('visibilitychange', sync);

		if ('ResizeObserver' in window) new ResizeObserver(() => layout()).observe(track);
		else window.addEventListener('resize', layout);

		renderState();
		renderPause();

		// Viewer
		let viewer = null;
		let current = 0;
		let opener = null;
		let parts = null;
		let finished = false;

		function build() {
			viewer = document.createElement('div');
			viewer.className = 'rv-viewer';
			viewer.setAttribute('role', 'dialog');
			viewer.setAttribute('aria-modal', 'true');
			if (text.title) viewer.setAttribute('aria-label', text.title);
			viewer.hidden = true;
			viewer.innerHTML =
				'<div class="rv-viewer__bar"><span class="rv-viewer__title"></span><span class="rv-viewer__counter" aria-live="polite"></span>' +
				`<button type="button" class="rv-viewer__close" aria-label="${text.close}">${ICONS.close}</button></div>` +
				'<div class="rv-viewer__stage"><video class="rv-viewer__video" controls playsinline preload="none"></video>' +
				`<button type="button" class="rv-viewer__prev" aria-label="${text.prev}">${ICONS.prev}</button>` +
				`<button type="button" class="rv-viewer__next" aria-label="${text.next}">${ICONS.next}</button></div>`;
			document.body.appendChild(viewer);
			parts = {
				title: viewer.querySelector('.rv-viewer__title'),
				counter: viewer.querySelector('.rv-viewer__counter'),
				video: viewer.querySelector('.rv-viewer__video'),
				close: viewer.querySelector('.rv-viewer__close'),
				prev: viewer.querySelector('.rv-viewer__prev'),
				next: viewer.querySelector('.rv-viewer__next'),
			};
			parts.title.textContent = text.title || '';
			parts.close.addEventListener('click', close);
			parts.prev.addEventListener('click', () => show(current - 1));
			parts.next.addEventListener('click', () => show(current + 1));
			parts.video.addEventListener('ended', () => { if (current === active) finished = true; });
			viewer.addEventListener('keydown', onKey);
			let vx = null;
			let vy = 0;
			const stage = viewer.querySelector('.rv-viewer__stage');
			stage.addEventListener('touchstart', (e) => { if (e.touches.length === 1) { vx = e.touches[0].clientX; vy = e.touches[0].clientY; } }, { passive: true });
			stage.addEventListener('touchend', (e) => {
				if (vx === null) return;
				const dx = e.changedTouches[0].clientX - vx;
				const dy = e.changedTouches[0].clientY - vy;
				vx = null;
				if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5) show(current + (dx < 0 ? 1 : -1));
			}, { passive: true });
		}

		function show(i) {
			current = wrap(i);
			const v = parts.video;
			v.pause();
			if (items[current].poster) v.setAttribute('poster', items[current].poster);
			else v.removeAttribute('poster');
			v.setAttribute('src', items[current].src);
			v.muted = false;
			parts.counter.textContent = `${current + 1} / ${N}`;
			const p = v.play();
			if (p && p.catch) p.catch(() => {});
		}

		function open(i, card) {
			if (!viewer) build();
			opener = card;
			finished = false;
			viewerOpen = true;
			sync();
			viewer.hidden = false;
			document.documentElement.classList.add('rv-viewer-open');
			show(i);
			parts.close.focus();
		}

		function close() {
			if (!viewer || viewer.hidden) return;
			parts.video.pause();
			parts.video.removeAttribute('src');
			parts.video.load();
			viewer.hidden = true;
			document.documentElement.classList.remove('rv-viewer-open');
			if (opener) opener.focus();
			viewerOpen = false;
			if (finished) go(active + 1);
			else sync();
		}

		function onKey(e) {
			if (e.key === 'Escape') { e.preventDefault(); close(); return; }
			if (e.key === 'ArrowRight' && e.target !== parts.video) { e.preventDefault(); show(current + 1); return; }
			if (e.key === 'ArrowLeft' && e.target !== parts.video) { e.preventDefault(); show(current - 1); return; }
			if (e.key === 'Tab') {
				const f = [parts.close, parts.video, parts.prev, parts.next];
				const idx = f.indexOf(document.activeElement);
				e.preventDefault();
				const nextIdx = e.shiftKey ? (idx <= 0 ? f.length - 1 : idx - 1) : (idx === -1 || idx === f.length - 1 ? 0 : idx + 1);
				f[nextIdx].focus();
			}
		}
	}

	function initAll() { document.querySelectorAll('[data-rv]').forEach(init); }
	if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
	else initAll();
	document.addEventListener('shopify:section:load', initAll);
})();
